import {
  listAllSkusForAdmin,
  getSkuWithLineas,
  upsertSku,
  setSkuActivo,
  upsertSkuLinea,
} from './catalogRepo.js';

/**
 * Panel de administración del catálogo (solo rol 'admin'; también reforzado
 * por RLS en Supabase, así que aunque alguien manipule el DOM no podrá
 * escribir sin el rol correcto).
 *
 * Enfoque v1: edición de las secciones técnicas (identificación, amasado,
 * velocidad, derivados, config_manual, empaque_mixto) como JSON estructurado
 * por línea, validado antes de enviar. Es deliberadamente simple y robusto
 * para una primera versión en producción; se puede evolucionar a formularios
 * campo-por-campo más adelante sin cambiar el modelo de datos.
 */

const LINEAS = ['A', 'B', 'C'];
const SECCIONES = ['identificacion', 'amasado', 'velocidad', 'derivados', 'config_manual', 'empaque_mixto'];

let allSkus = [];

export async function renderAdminList(filtro = '') {
  const box = document.getElementById('adminLista');
  if (!box) return;
  try {
    allSkus = await listAllSkusForAdmin();
  } catch (err) {
    box.innerHTML = `<div class="empty-state">Error cargando referencias: ${escapeHtml(err.message)}</div>`;
    return;
  }
  const q = filtro.trim().toUpperCase();
  const rows = allSkus.filter(s => !q || s.codigo.toUpperCase().includes(q) || (s.descripcion || '').toUpperCase().includes(q));
  if (rows.length === 0) {
    box.innerHTML = '<div class="empty-state">Sin resultados.</div>';
    return;
  }
  box.innerHTML = rows.map(s => `
    <div class="resultado-item" data-codigo="${escapeHtml(s.codigo)}">
      <div class="resultado-info"><b>${escapeHtml(s.codigo)}</b> — <span class="resultado-desc">${escapeHtml(s.descripcion)}</span></div>
      <div class="resultado-lineas">
        <span class="pill ${s.activo ? 'si' : 'no'}">${s.activo ? 'ACTIVO' : 'INACTIVO'}</span>
      </div>
    </div>`).join('');
  box.querySelectorAll('.resultado-item').forEach(el => {
    el.addEventListener('click', () => renderEditor(el.dataset.codigo));
  });
}

export function renderNuevoSkuForm() {
  renderEditorShell({ codigo: '', descripcion: '', activo: true, lineas: [] }, true);
}

async function renderEditor(codigo) {
  const cont = document.getElementById('adminEditor');
  cont.innerHTML = '<div class="hint">Cargando…</div>';
  try {
    const sku = await getSkuWithLineas(codigo);
    renderEditorShell(sku, false);
  } catch (err) {
    cont.innerHTML = `<div class="empty-state">Error: ${escapeHtml(err.message)}</div>`;
  }
}

function seccionValue(sku, letra, campo) {
  const linea = (sku.lineas || []).find(l => l.linea === letra);
  const val = linea ? linea[campo] : null;
  return val ? JSON.stringify(val, null, 2) : (campo === 'config_manual' || campo === 'empaque_mixto' ? '' : '{}');
}

function boolChecked(sku, letra, campo) {
  const linea = (sku.lineas || []).find(l => l.linea === letra);
  return linea ? Boolean(linea[campo]) : false;
}

function renderEditorShell(sku, esNuevo) {
  const cont = document.getElementById('adminEditor');
  cont.innerHTML = `
    <div class="panel">
      <h2><span class="n">✎</span>${esNuevo ? 'Nueva referencia' : 'Editar ' + escapeHtml(sku.codigo)}</h2>
      <div class="kv-grid" style="margin-bottom:14px;">
        <label style="grid-column:span 2;">Código
          <input type="text" id="edCodigo" value="${escapeHtml(sku.codigo)}" ${esNuevo ? '' : 'readonly'} placeholder="Ej: 30020101">
        </label>
        <label style="grid-column:span 3;">Descripción
          <input type="text" id="edDescripcion" value="${escapeHtml(sku.descripcion || '')}" placeholder="Descripción completa">
        </label>
        <label style="display:flex;align-items:center;gap:6px;">
          <input type="checkbox" id="edActivo" ${sku.activo !== false ? 'checked' : ''}> Activo
        </label>
      </div>

      ${LINEAS.map(letra => `
        <div class="linea-block">
          <div class="linea-block-head">
            <span class="tag">LÍNEA ${letra}</span>
            <label style="font-size:11.5px;display:flex;align-items:center;gap:6px;">
              <input type="checkbox" id="ed_${letra}_produce" ${boolChecked(sku, letra, 'produce') ? 'checked' : ''}> Produce
            </label>
            <label style="font-size:11.5px;display:flex;align-items:center;gap:6px;">
              <input type="checkbox" id="ed_${letra}_excepcion" ${boolChecked(sku, letra, 'excepcion_config') ? 'checked' : ''}> Excepción config.
            </label>
          </div>
          <div class="linea-block-body">
            ${SECCIONES.map(sec => `
              <div class="hint" style="margin-top:8px;">${sec}${(sec === 'config_manual' && letra !== 'A') || (sec === 'empaque_mixto' && letra !== 'C') ? ' (normalmente vacío en esta línea)' : ''}</div>
              <textarea id="ed_${letra}_${sec}" rows="4" style="width:100%;font-family:var(--mono);font-size:11.5px;background:var(--bg);color:var(--text);border:1px solid var(--line);border-radius:5px;padding:8px;">${escapeHtml(seccionValue(sku, letra, sec))}</textarea>
            `).join('')}
          </div>
        </div>
      `).join('')}

      <div id="adminEditorError" class="auth-error" style="display:none;"></div>
      <div class="filtros">
        <button class="chip active" id="btnGuardarSku">Guardar cambios</button>
        <button class="chip" id="btnCancelarSku">Cancelar</button>
      </div>
    </div>`;

  document.getElementById('btnCancelarSku').addEventListener('click', () => { cont.innerHTML = ''; });
  document.getElementById('btnGuardarSku').addEventListener('click', () => guardarSku(esNuevo));
}

async function guardarSku(esNuevo) {
  const errBox = document.getElementById('adminEditorError');
  errBox.style.display = 'none';
  const codigo = document.getElementById('edCodigo').value.trim();
  const descripcion = document.getElementById('edDescripcion').value.trim();
  const activo = document.getElementById('edActivo').checked;

  if (!codigo || !descripcion) {
    errBox.textContent = 'Código y descripción son obligatorios.';
    errBox.style.display = 'block';
    return;
  }

  const lineasPayload = [];
  for (const letra of LINEAS) {
    const produce = document.getElementById(`ed_${letra}_produce`).checked;
    const excepcion_config = document.getElementById(`ed_${letra}_excepcion`).checked;
    const seccionData = {};
    try {
      for (const sec of SECCIONES) {
        const raw = document.getElementById(`ed_${letra}_${sec}`).value.trim();
        seccionData[sec] = raw ? JSON.parse(raw) : (sec === 'config_manual' || sec === 'empaque_mixto' ? null : {});
      }
    } catch (e) {
      errBox.textContent = `JSON inválido en Línea ${letra}: ${e.message}`;
      errBox.style.display = 'block';
      return;
    }
    lineasPayload.push({ sku_codigo: codigo, linea: letra, produce, excepcion_config, ...seccionData });
  }

  try {
    await upsertSku({ codigo, descripcion, activo });
    for (const payload of lineasPayload) {
      await upsertSkuLinea(payload);
    }
  } catch (err) {
    errBox.textContent = `Error guardando: ${err.message}`;
    errBox.style.display = 'block';
    return;
  }

  document.getElementById('adminEditor').innerHTML = `<div class="hint">Guardado ✓. Los cambios se reflejarán en el catálogo en tiempo real.</div>`;
  renderAdminList(document.getElementById('adminBuscador')?.value || '');
}

export async function desactivarSku(codigo) {
  await setSkuActivo(codigo, false);
  renderAdminList(document.getElementById('adminBuscador')?.value || '');
}

function escapeHtml(str) {
  return String(str ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

export function initAdminUI() {
  const buscador = document.getElementById('adminBuscador');
  const btnNuevo = document.getElementById('btnNuevoSku');
  if (buscador) buscador.addEventListener('input', e => renderAdminList(e.target.value));
  if (btnNuevo) btnNuevo.addEventListener('click', () => renderNuevoSkuForm());
}
